import { Type, inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ImmomioModalBase, ImmomioModalService } from '@immomio/ui/modal';
import { EMPTY, catchError, pipe, switchMap, tap } from 'rxjs';
import { INFRASTRUCTURE_CONFIG } from '@resident-nx/web/data-access';

import { FeedbackFacade } from './feedback.facade';
import {
  IResidentFeedbackInput,
  ResidentFeedbackCategory,
  ResidentFeedbackResult,
} from '@resident-nx/web/feedback/model';

export type FeedbackStep = 'ask' | 'form' | 'thanks';

type FeedbackState = {
  step: FeedbackStep;
  /**
   * What will be reported when the modal closes. Starts as `DISMISSED` and is upgraded
   * as the user acts, so whatever they last chose is what gets recorded.
   */
  result: ResidentFeedbackResult;
  category: ResidentFeedbackCategory | null;
  comment: string;
  /** Guards against reporting twice for a single prompt. */
  reported: boolean;
  /** The backend is only asked once per browser session, however many success events fire. */
  promptedThisSession: boolean;
};

/**
 * A coarse primary pointer means a touch device, so phones and tablets report `WEB_MOBILE`.
 * This tracks the device rather than the window: resizing a desktop browser does not flip it.
 *
 * `matchMedia` is called optionally because jsdom does not implement it — under the unit-test
 * environment this falls back to `WEB_OTHER` rather than throwing.
 */
const isMobileBrowser = (): boolean => window.matchMedia?.('(pointer: coarse)')?.matches ?? false;

const initialState: FeedbackState = {
  step: 'ask',
  result: 'DISMISSED',
  category: null,
  comment: '',
  reported: false,
  promptedThisSession: false,
};

export const FeedbackStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      facade = inject(FeedbackFacade),
      modalService = inject(ImmomioModalService),
      config = inject(INFRASTRUCTURE_CONFIG)
    ) => {
      const buildMeta = (): Pick<
        IResidentFeedbackInput,
        'platform' | 'appVersion' | 'osVersion'
      > => ({
        // `osVersion` is intentionally omitted on web: it would need user-agent parsing,
        // and platform plus appVersion already identify the client well enough.
        platform: isMobileBrowser() ? 'WEB_MOBILE' : 'WEB_OTHER',
        appVersion: config.environment.appVersion,
      });

      const send = rxMethod<IResidentFeedbackInput>(
        pipe(switchMap(input => facade.saveFeedback(input).pipe(catchError(() => EMPTY))))
      );

      // The payload is wrapped in an object on purpose: `rxMethod` treats a bare
      // function input as a signal getter, and a component type is a function.
      const checkEligibility = rxMethod<{ modal: Type<ImmomioModalBase> }>(
        pipe(
          switchMap(({ modal }) =>
            facade.isEligible().pipe(
              tap(eligible => {
                if (!eligible) {
                  return;
                }
                patchState(store, { ...initialState, promptedThisSession: true });
                modalService.open(modal);
              }),
              catchError(() => EMPTY)
            )
          )
        )
      );

      /** Writes the accumulated feedback. Every prompt results in exactly one call. */
      const reportOnce = (): void => {
        if (store.reported()) {
          return;
        }
        patchState(store, { reported: true });

        const category = store.category();
        const comment = store.comment();

        send({
          result: store.result(),
          ...(category ? { category } : {}),
          ...(comment ? { comment } : {}),
          ...buildMeta(),
        });
      };

      return {
        /**
         * Asks the backend whether this user may be prompted and, if so, opens the given modal.
         * The component type is passed in by the caller so this lib never imports a feature lib.
         */
        promptIfEligible(modal: Type<ImmomioModalBase>): void {
          if (store.promptedThisSession()) {
            return;
          }
          patchState(store, { promptedThisSession: true });
          checkEligibility({ modal });
        },

        /** A completed answer — reported straight away. */
        choosePositive(): void {
          patchState(store, { step: 'thanks', result: 'POSITIVE' });
          reportOnce();
        },

        /**
         * Only opens the comment form; nothing is written yet, because the user may still
         * add a category and a comment. Abandoning the form reports a bare `NEGATIVE`.
         */
        chooseNegative(): void {
          patchState(store, { step: 'form', result: 'NEGATIVE' });
        },

        /** The completed negative form — reported straight away. */
        addDetails(details: {
          category?: ResidentFeedbackCategory | null;
          comment?: string;
        }): void {
          patchState(store, {
            step: 'thanks',
            result: 'NEGATIVE',
            category: details.category ?? null,
            comment: details.comment ?? '',
          });
          reportOnce();
        },

        /**
         * Fired when the modal goes away. A no-op if the user completed an answer; otherwise it
         * reports what they reached — `DISMISSED` on the first step, `NEGATIVE` on the form.
         */
        report(): void {
          reportOnce();
        },
      };
    }
  )
);
